import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, ShieldCheck, KeyRound, Trash2, Ban, Copy, Loader2, RotateCcw, ShieldOff } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { authApi } from "../../lib/auth";
import { errorMessage } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }) : "never");

function TempPassword({ value }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Temporary password copied");
    } catch {
      toast(value);
    }
  };
  return (
    <div className="temp-pass" data-testid="temp-password">
      <span className="mono">{value}</span>
      <button type="button" className="tb-btn sm" onClick={copy}>
        <Copy size={13} /> Copy
      </button>
    </div>
  );
}

function NewUserDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", role: "user", temp_password: "" });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await authApi.org.createUser({ ...form, temp_password: form.temp_password || undefined });
      setCreated(u);
      onCreated(u);
    } catch (err) {
      toast.error("Could not create user", { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-user-dialog">
        {created ? (
          <>
            <DialogHeader>
              <DialogTitle>User created</DialogTitle>
              <DialogDescription>
                Share this temporary password with <b>{created.email}</b> through a secure channel. They must change it at first sign-in. It will not be shown again.
              </DialogDescription>
            </DialogHeader>
            <TempPassword value={created.temp_password} />
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)} data-testid="new-user-done">
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={submit}>
            <DialogHeader>
              <DialogTitle>Add a user</DialogTitle>
              <DialogDescription>Admins can link tenants, run scans and manage users. Standard users can view tenants and scan results.</DialogDescription>
            </DialogHeader>
            <div className="ws-form">
              <label className="auth-field">
                <span>Name</span>
                <Input value={form.name} onChange={set("name")} required data-testid="new-user-name" />
              </label>
              <label className="auth-field">
                <span>Email</span>
                <Input type="email" value={form.email} onChange={set("email")} required data-testid="new-user-email" />
              </label>
              <label className="auth-field">
                <span>Role</span>
                <select value={form.role} onChange={set("role")} className="ws-native-select" data-testid="new-user-role">
                  <option value="user">Standard user</option>
                  <option value="admin">Admin</option>
                </select>
              </label>
              <label className="auth-field">
                <span>Temporary password (optional - generated if blank)</span>
                <Input value={form.temp_password} onChange={set("temp_password")} minLength={10} autoComplete="off" data-testid="new-user-temp" />
              </label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy} data-testid="new-user-submit">
                {busy && <Loader2 size={14} className="spin" />} Create user
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tempPw, setTempPw] = useState(null);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(() => authApi.org.users().then(setUsers).catch((err) => toast.error("Could not load users", { description: errorMessage(err) })), []);
  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, fn, ok) => {
    setBusyId(id);
    try {
      const res = await fn();
      if (ok) toast.success(ok);
      await load();
      return res;
    } catch (err) {
      toast.error("Action failed", { description: errorMessage(err) });
      return null;
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="ws-page" data-testid="users-page">
      <header className="ws-head">
        <div>
          <h1>Users</h1>
          <p>People in your organization who can sign in to this workspace.</p>
        </div>
        <button type="button" className="tb-btn primary" onClick={() => setDialogOpen(true)} data-testid="add-user-btn">
          <Plus size={15} /> Add user
        </button>
      </header>

      {tempPw && (
        <div className="ws-card glass" data-testid="reset-password-result">
          <h2>New temporary password for {tempPw.email}</h2>
          <p className="muted">Share it securely - they'll be asked to change it at next sign-in.</p>
          <TempPassword value={tempPw.temp_password} />
          <button type="button" className="tb-btn sm ghost" onClick={() => setTempPw(null)}>
            Dismiss
          </button>
        </div>
      )}

      <section className="ws-card glass">
        <div className="ws-table-wrap">
          <table className="ws-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Security</th>
                <th>Last sign-in</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(users || []).map((u) => {
                const self = u.id === me.id;
                const busy = busyId === u.id;
                return (
                  <tr key={u.id} data-testid={`user-row-${u.email}`}>
                    <td>
                      <div className="sku-name">
                        {u.name} {self && <span className="mini-chip">you</span>}
                      </div>
                      <div className="sku-part mono">{u.email}</div>
                    </td>
                    <td>
                      <select
                        value={u.role}
                        disabled={self || busy}
                        onChange={(e) => act(u.id, () => authApi.org.patchUser(u.id, { role: e.target.value }), "Role updated")}
                        className="ws-native-select sm"
                        data-testid={`user-role-${u.email}`}
                      >
                        <option value="user">Standard user</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td>
                      <div className="lic-chips">
                        {u.mfa_enabled ? (
                          <span className="mini-chip ok">
                            <ShieldCheck size={10} /> MFA on
                          </span>
                        ) : (
                          <span className="mini-chip warn">
                            <ShieldOff size={10} /> no MFA
                          </span>
                        )}
                        {u.must_change_password && <span className="mini-chip">temp password</span>}
                      </div>
                    </td>
                    <td>{fmt(u.last_login_at)}</td>
                    <td>{u.disabled ? <span className="status-pill warn">Disabled</span> : <span className="status-pill ok">Active</span>}</td>
                    <td>
                      <div className="tenant-actions right">
                        <button type="button" className="tb-btn sm" disabled={busy} title="Issue a new temporary password" onClick={() => act(u.id, () => authApi.org.resetPassword(u.id)).then((r) => r && setTempPw({ email: u.email, temp_password: r.temp_password }))} data-testid={`user-reset-pw-${u.email}`}>
                          <KeyRound size={13} /> Reset password
                        </button>
                        {u.mfa_enabled && (
                          <button type="button" className="tb-btn sm" disabled={busy} title="Remove the user's authenticator so they can enroll again" onClick={() => window.confirm(`Reset MFA for ${u.email}?`) && act(u.id, () => authApi.org.resetMfa(u.id), "MFA reset")} data-testid={`user-reset-mfa-${u.email}`}>
                            <RotateCcw size={13} /> Reset MFA
                          </button>
                        )}
                        {!self && (
                          <>
                            <button type="button" className="tb-btn sm" disabled={busy} onClick={() => act(u.id, () => authApi.org.patchUser(u.id, { disabled: !u.disabled }), u.disabled ? "User enabled" : "User disabled")} data-testid={`user-toggle-${u.email}`}>
                              <Ban size={13} /> {u.disabled ? "Enable" : "Disable"}
                            </button>
                            <button type="button" className="tb-btn sm ghost danger" disabled={busy} onClick={() => window.confirm(`Delete ${u.email}?`) && act(u.id, () => authApi.org.deleteUser(u.id), "User deleted")} data-testid={`user-delete-${u.email}`}>
                              <Trash2 size={13} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      {dialogOpen && <NewUserDialog open onOpenChange={setDialogOpen} onCreated={load} />}
    </div>
  );
}
